import {
  Box,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { Crumb } from '../../../components/Breadcrumbs/interfaces';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { LoadingLayover } from '../../../components/LoadingLayover';
import { FloatingActionsButtons } from '../../../components/FloatingActionsButtons';
import { EditAction } from '../../../components/FloatingActionsButtons/components/EditAction';
import { DeleteAction } from '../../../components/FloatingActionsButtons/components/DeleteAction';
import { useDeleteAgent, useGetAgent } from '../../../reactQuery/Agents/agents';
import { UpsertAgentDialog } from '../dialogs/UpsertAgentDialog';
import { DeleteAgentDialog } from '../dialogs/DeleteAgentDialog';
import { useAuth } from '../../../context/AuthProvider';
import { BORDER_COLOR } from '../../../constants';
import { AgentAvatar } from '../../../components/AgentAvatar';

export const AgentDetails: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

  const { mutate: deleteAgent, isLoading: isDeletingAgent } = useDeleteAgent();
  const { auth } = useAuth();
  const currentUser = auth?.user;

  const navigate = useNavigate();
  const params = useParams();

  const agentId = Number(params.id);
  const crumbs: Crumb[] = [
    {
      title: t('breadcrumbs.overview'),
      link: '/agents',
    },
  ];

  const TableCellHeading = styled(TableCell)(() => ({
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  }));

  const { data: agent, isLoading, error } = useGetAgent(agentId);

  const handleAgentNotFound = (): void => {
    enqueueSnackbar(
      t('errors.resourceNotFound', {
        resource: 'agent',
      }),
      {
        variant: 'error',
      }
    );
    navigate('/agents');
  };

  useEffect(() => {
    if (Number.isNaN(agentId)) {
      handleAgentNotFound();
    } else if (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        handleAgentNotFound();
      } else {
        enqueueSnackbar(t('errors.somethingWentWrong'), {
          variant: 'error',
        });
      }
    }
  }, [agentId, error, navigate]);

  if (isLoading) {
    return <LoadingLayover />;
  }

  if (!agent) {
    navigate('/agents');
    return <Box>Error</Box>;
  }

  const isCurrentUser = currentUser?.id === agentId;

  const onDeleteAgent = (): void => {
    deleteAgent(agent.id, {
      onSuccess: () => {
        enqueueSnackbar(t('agents:prompts.deleteAgent.success'), {
          variant: 'success',
        });
        navigate('/agents');
      },
      onError: () => {
        enqueueSnackbar(t('agents:prompts.deleteAgent.failed'), {
          variant: 'error',
        });
      },
    });
  };

  return (
    <Fragment>
      <PageTitle title={t('agents:title')} space={'double'}>
        <Breadcrumbs
          crumbs={crumbs}
          current={`${agent.firstName} ${agent.lastName}`}
        />
      </PageTitle>

      <Paper
        sx={{
          display: 'flex',
        }}
      >
        <AgentAvatar
          agent={agent}
          variant="profile"
          sx={{
            margin: '10px',
          }}
        />

        <TableContainer
          sx={{
            borderLeft: `1px solid ${BORDER_COLOR}`,
          }}
        >
          <Table>
            <TableBody>
              <TableRow>
                <TableCellHeading width="50px">
                  {t('entities:common.firstName')}
                </TableCellHeading>
                <TableCell>{agent.firstName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCellHeading width="50px">
                  {t('entities:common.lastName')}
                </TableCellHeading>
                <TableCell>{agent.lastName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCellHeading width="50px">
                  {t('entities:common.email')}
                </TableCellHeading>
                <TableCell>{agent.email}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <FloatingActionsButtons>
        <DeleteAction
          disabled={isCurrentUser}
          onClick={() => {
            setShowDeleteDialog(true);
          }}
        />
        <EditAction
          onClick={() => {
            setShowEditDialog(true);
          }}
        />
      </FloatingActionsButtons>
      <UpsertAgentDialog
        agent={agent}
        show={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      />
      <DeleteAgentDialog
        show={showDeleteDialog}
        agentName={`${agent.firstName} ${agent.lastName}`}
        disabled={isDeletingAgent}
        onConfirm={onDeleteAgent}
        onClose={() => setShowDeleteDialog(false)}
      />
    </Fragment>
  );
};
